import { callDeepLink } from '@croquiscom/web2app';
import { vars } from '@croquiscom-pvt/zds/tokens';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';

import { useAppConfig } from '@common/app-config';
import { getAdjustedBrowsingType } from '@common/app-manager';
import { useUBLContext, ZigzagEvent } from '@common/log-manager';
import { Toast } from '@common/overlay-manager';
import { gotoLogin } from '@common/utils';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { getSelectionOptionStorageKey, useIsAuthenticated } from '@domains/pdp-common';

import { SelectBoxItemOptionValue } from '../../components/SelectBox/select-box-item';
import { SelectBox } from '../../components/SelectBox/SelectBox';
import { TextField } from '../../components/TextField';
import { registerWmsUserNotification } from '../../graphql/select-option.2';
import { PdpBrowsingType } from '../../graphql/select-option_types.2';
import { WmsUserNotificationType } from '../../graphql/select-option_types.2';
import { useOptionsControl } from '../../hooks/useOptionsControl';
import { useSelectOptionLog } from '../../log/useSelectOptionLog';
import { useSelectOptionService } from '../../services/useSelectOptionService';

interface OptionContainerProps {
  disabledAnimation?: boolean;
  disabledCouponPrice?: boolean;
  disabledPrice?: boolean;
  disabledNotification?: boolean;
}

export function OptionContainer({
  disabledAnimation = false,
  disabledCouponPrice,
  disabledPrice,
  disabledNotification,
}: OptionContainerProps) {
  const {
    meta_catalog_product_info,
    options: {
      catalog_product: { id: product_id, shop_id, matching_catalog_product_info },
    },
    is_zigzin_product,
    is_zigzin_matching_product,
    isGeneralMatchingProduct,
  } = useSelectOptionService();

  const ublContext = useUBLContext();
  const { isLoggedIn } = useIsAuthenticated();
  const { isZigzagApp } = useAppConfig();
  const {
    is_bogo,
    setRestockNotiStatus,
    options,
    refined_options,
    textOptions,
    optionValues,
    isRestockNotiDrawerShown,
    firstSoldOutIndex,
    hasFastShippingOption,
    checkSelectBoxActive,
    checkAccessible,
    activateRestockNotiDrawer,
    enterOption,
    activateOption,
    setActiveParentIndex,
    setActiveIndex,
    requestRestockNoti,
    selectOption,
    handleOptionClick,
  } = useOptionsControl();
  const { impressSoldOut, impressMetaPDPLink, impressOption, clickMetaPDPLink, clickSoldOutViewMore, clickOption } =
    useSelectOptionLog();
  const router = useRouter();
  const isUseToast = !!router.query.use_toast;

  const refinedOptionValues = optionValues.map((option) => ({
    ...option,
    price: disabledCouponPrice ? option.origin_price : option.price,
  }));
  const sendSoldOutImpressionLog = (value: string) => {
    if (is_zigzin_product) {
      impressSoldOut(product_id, value);
    }
  };

  const sendOptionImpressionLog = (value: string, isZigzinType: boolean) => {
    const matchingProductId = matching_catalog_product_info?.id ?? null;
    const generalProductId = is_zigzin_matching_product && !isZigzinType ? matchingProductId : null;
    const zonlyProductId = isGeneralMatchingProduct && isZigzinType ? matchingProductId : null;

    impressOption(value, product_id, generalProductId, zonlyProductId);
  };

  const handleImpressMetaPDPLink = () => {
    if (is_zigzin_product) {
      impressMetaPDPLink(product_id, is_zigzin_matching_product ? matching_catalog_product_info?.id : null);
    }
  };

  const handleMetaPDPLinkClick = () => {
    const pdp_url = meta_catalog_product_info?.pdp_url ?? matching_catalog_product_info?.pdp_url;

    if (!pdp_url) {
      return;
    }

    const meta_catalog_product_id = meta_catalog_product_info?.id ?? matching_catalog_product_info?.id;
    const browsing_type = meta_catalog_product_info?.browsing_type ?? matching_catalog_product_info?.browsing_type;

    clickMetaPDPLink(product_id, is_zigzin_matching_product ? matching_catalog_product_info?.id : null);

    if (browsing_type && browsing_type !== PdpBrowsingType.NORMAL) {
      callDeepLink('/product_detail')({
        browsing_type: getAdjustedBrowsingType(browsing_type, pdp_url),
        url: pdp_url,
        shop_id,
        shop_product_no: null,
        catalog_product_id: meta_catalog_product_id,
      });
    } else {
      callDeepLink(`${pdp_url}`)();
    }
  };

  const handleRestockNotiToggle = async (
    { notification_info, value }: SelectBoxItemOptionValue,
    currentActive: boolean,
  ) => {
    if (!isLoggedIn) {
      sessionStorage.setItem(getSelectionOptionStorageKey(product_id), 'opened');
      gotoLogin({
        redirect_url: !isZigzagApp ? window.location.href : '',
      });
      return;
    }

    const input = {
      product_item_id: notification_info?.product_item_id ?? '',
      active: currentActive,
    };

    try {
      if (!input.product_item_id) {
        showRestockNotifyError();
        return;
      }
      await registerWmsUserNotification({
        input: { ...input, notification_type: WmsUserNotificationType.RE_WAREHOUSED_IN },
      });

      if (currentActive) {
        if (isZigzagApp && isUseToast) {
          Toast.show(
            '재입고 시 카카오톡과 푸시 알림으로 알려드릴게요.\n(전화번호가 없는 경우 푸시 알림만 발송됩니다.)',
          );
        } else {
          setRestockNotiStatus('succeeded');
        }
      } else {
        if (isZigzagApp && isUseToast) {
          Toast.show('재입고 알림이 취소되었어요.');
        } else {
          Toast.show('알림이 취소되었습니다.');
        }
      }

      requestRestockNoti(input);
      ZigzagEvent.subl({
        category: 'click',
        navigation: ublContext.navigation ? ublContext.navigation : 'zpay_option',
        object_type: 'button',
        object_id: currentActive ? 'restock_noti' : 'restock_cancel',
        data: JSON.stringify({ catalog_product_id: product_id, product_option: value }),
      });
    } catch (e) {
      console.warn(e);
      showRestockNotifyError();
    }
  };

  const handleOptionSelect =
    (name: string, id: string, parent_key: number, child_key: number) => (ov: SelectBoxItemOptionValue) => {
      if (is_zigzin_product && ov.sold_out && firstSoldOutIndex === -1) {
        activateRestockNotiDrawer({ active: true, index: child_key });
        clickSoldOutViewMore(id, ov.value);
      }

      if (is_zigzin_product || !ov.sold_out) {
        selectOption({ name, id, value: ov.value, value_id: ov.id, is_zigzin: !!ov.is_zigzin });

        const generalProductId = is_zigzin_product && !ov.is_zigzin ? matching_catalog_product_info?.id : null;
        const zonlyProductId = !is_zigzin_product && ov.is_zigzin ? matching_catalog_product_info?.id : null;

        clickOption(ov.id, product_id, generalProductId, zonlyProductId);

        if (options[parent_key].length - 1 === child_key) {
          const is_last = options.length - 1 < parent_key + 1;
          const next_parent_key = is_last ? 0 : parent_key + 1;
          const has_required_text_option = textOptions[next_parent_key].find((o) => o.is_required && !o.selected_value);

          const active_parent_index = has_required_text_option ? -1 : next_parent_key;
          const active_index = has_required_text_option ? -1 : 0;

          setActiveParentIndex(active_parent_index);
          setActiveIndex(active_index);
        } else {
          setActiveIndex((index) => (index += 1));
        }
      }
    };

  const showRestockNotifyError = () => {
    if (isZigzagApp && isUseToast) {
      Toast.show('재입고 알림 신청에 실패했습니다.\n다시 시도해주세요.');
    } else {
      setRestockNotiStatus('failed');
    }
  };

  return (
    <>
      {refined_options.map((option_items, option_key) => {
        const visible_header = options.length > 1 && !isRestockNotiDrawerShown;
        const current_text_options = textOptions[option_key];

        return (
          <SelectOptionContainer key={`select-option-container--${option_key}`}>
            {visible_header && (
              <header>
                상품 {option_key + 1}
              </header>
            )}
            {!isRestockNotiDrawerShown &&
              current_text_options.map(({ name, is_required, max_length, selected_value }) => (
                <TextField
                  key={name}
                  name={name}
                  is_required={is_required}
                  max_length={Number(max_length)}
                  value={selected_value}
                  onFocus={() => {
                    setActiveParentIndex(option_key);
                    setActiveIndex(-1);
                  }}
                  onChange={(v) => {
                    enterOption({ name, value: v });
                  }}
                  onKeyPress={(code) => {
                    if (code === 13) {
                      activateOption();
                    }
                  }}
                />
              ))}
            {option_items.map(({ name, id, selected_value }, option_items_key) => {
              if (firstSoldOutIndex >= option_items_key) {
                return null;
              }
              return (
                <SelectBox
                  key={`${option_key}-${name}-${option_items_key}`}
                  name={selected_value || name}
                  options={refinedOptionValues}
                  active={checkSelectBoxActive(option_key, option_items_key)}
                  isAccessible={checkAccessible(option_key, option_items_key)}
                  isLastOption={option_items_key === option_items.length - 1}
                  isFirstSoldOutOption={!isRestockNotiDrawerShown}
                  isZigzin={is_zigzin_product}
                  disabledPrice={disabledPrice}
                  isMatchingProduct={Boolean(matching_catalog_product_info)}
                  isMetaLinkAvailable={
                    meta_catalog_product_info?.is_able_to_buy ?? matching_catalog_product_info?.is_able_to_buy
                  }
                  is_bogo={is_bogo}
                  hasFastShippingOption={hasFastShippingOption}
                  onClick={handleOptionClick(option_key, option_items_key)}
                  onSelect={handleOptionSelect(name, id, option_key, option_items_key)}
                  onImpress={sendOptionImpressionLog}
                  onImpressSoldout={sendSoldOutImpressionLog}
                  onRestockNotiClick={handleRestockNotiToggle}
                  onImpressMetaPDPLink={handleImpressMetaPDPLink}
                  onMetaPDPLinkClick={handleMetaPDPLinkClick}
                  disabledAnimation={disabledAnimation}
                  disabledNotification={disabledNotification}
                />
              );
            })}
          </SelectOptionContainer>
        );
      })}
    </>
  );
}

const SelectOptionContainer = styled.div`
  header {
    margin-bottom: 8px;
    font-weight: 600;
    color: ${vars.v3Color.gray700};
    font-size: 14px;
  }
`;
